/**
 * AgencyHome.jsx — agency teammate's landing.
 *
 *   - Top: Intro-call queue (requested · confirmed · upcoming) with confirm/decline.
 *   - Below: active engagements list (one card per founder).
 *
 * No filters yet — Sprint 5's Agency Command Centre will add the full kanban
 * across clients + revenue tracker + health scores.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import { toast } from 'sonner';
import { Briefcase, PhoneCall, Check, X, ArrowRight, Calendar, Clock } from '@phosphor-icons/react';

const ACCENT = '#6366f1';

const fmtDate = (s) => s ? new Date(s).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const AgencyHome = () => {
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [engagements, setEngagements] = useState([]);
  const [health, setHealth] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [c, e, h] = await Promise.all([
        api.get('/intro-calls/mine'),
        api.get('/engagements/mine'),
        api.get('/agency/health-scores').catch(() => ({ data: { health: {} } })),
      ]);
      setCalls(c.data.intro_calls || []);
      setEngagements(e.data.engagements || []);
      setHealth(h.data.health || {});
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const updateCall = async (call, status) => {
    try {
      await api.patch(`/intro-calls/${call.id}`, { status });
      toast.success(`Call ${status}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const pendingCalls = calls.filter((c) => c.status === 'requested');
  const upcomingCalls = calls.filter((c) => c.status === 'confirmed');

  if (loading) return <div className="p-12 text-sm text-text-muted">Loading…</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1280px] mx-auto" data-testid="agency-home">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-2" style={{ color: ACCENT }}>Founderboard Studios — Agency</div>
        <h1 className="text-3xl sm:text-4xl font-heading font-bold text-text-primary leading-[1.05] mb-2" style={{ letterSpacing: '-0.02em' }}>
          Your engagements
        </h1>
        <p className="text-sm text-text-secondary">Confirm intro calls, draft SOWs, and run founder requests through the pipeline.</p>
      </div>

      {/* Intro call queue */}
      {(pendingCalls.length > 0 || upcomingCalls.length > 0) && (
        <div className="mb-8">
          <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted mb-3 inline-flex items-center gap-1.5"><PhoneCall size={11} weight="bold" /> Intro calls</div>
          <div className="space-y-2" data-testid="agency-intro-calls">
            {pendingCalls.map((c) => (
              <div key={c.id} className="bg-surface border border-border rounded-lg p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-testid={`agency-intro-call-${c.id}`}>
                <div>
                  <div className="text-sm font-medium text-text-primary">{c.founder_startup_name || c.founder_full_name}</div>
                  <div className="text-[11px] text-text-muted">{c.founder_email} · proposed {fmtDate(c.proposed_slot)}</div>
                  {c.founder_notes && <div className="text-xs text-text-secondary mt-1 italic">&ldquo;{c.founder_notes}&rdquo;</div>}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => updateCall(c, 'declined')} data-testid={`agency-intro-call-decline-${c.id}`} className="px-3 py-1.5 text-xs rounded-lg border border-border text-text-muted hover:text-danger hover:border-danger">
                    <X size={11} weight="bold" className="inline mr-1" /> Decline
                  </button>
                  <button onClick={() => updateCall(c, 'confirmed')} data-testid={`agency-intro-call-confirm-${c.id}`} className="px-3 py-1.5 text-xs rounded-lg text-white font-semibold inline-flex items-center gap-1" style={{ backgroundColor: ACCENT }}>
                    <Check size={11} weight="bold" /> Confirm
                  </button>
                </div>
              </div>
            ))}
            {upcomingCalls.map((c) => (
              <div key={c.id} className="bg-success/5 border border-success/30 rounded-lg p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-testid={`agency-intro-call-upcoming-${c.id}`}>
                <div>
                  <div className="text-sm font-medium text-text-primary inline-flex items-center gap-2">
                    <Check size={11} weight="bold" className="text-success" /> {c.founder_startup_name || c.founder_full_name}
                  </div>
                  <div className="text-[11px] text-text-muted">Confirmed · {fmtDate(c.proposed_slot)}</div>
                </div>
                <button onClick={() => updateCall(c, 'completed')} className="px-3 py-1.5 text-xs rounded-lg border border-border text-text-muted hover:text-text-primary">
                  Mark as completed
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Engagements */}
      <div>
        <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted mb-3 inline-flex items-center gap-1.5"><Briefcase size={11} weight="bold" /> Engagements ({engagements.length})</div>
        {engagements.length === 0 ? (
          <div className="border border-dashed border-border rounded-xl p-10 text-center text-sm text-text-muted">
            No active engagements yet. Confirm an intro call above — the founder can then start an engagement.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="agency-engagements">
            {engagements.map((e) => {
              const h = health[e.id];
              const bandColor = h?.band === 'healthy' ? '#10b981' : h?.band === 'warning' ? '#f59e0b' : '#ef4444';
              return (
              <button key={e.id} onClick={() => navigate(`/agency/engagements/${e.id}`)} data-testid={`agency-engagement-card-${e.id}`} className="text-left bg-surface border border-border rounded-xl p-5 hover:border-text-primary transition-colors">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: ACCENT }}>{e.status}</span>
                  {h && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: `${bandColor}1a`, color: bandColor }} data-testid={`agency-engagement-health-${e.id}`}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: bandColor }} />
                      {h.score} · {h.band.replace('_', ' ')}
                    </span>
                  )}
                </div>
                <h3 className="font-heading font-bold text-text-primary text-base mb-1">{e.founder_startup_name || e.founder_full_name}</h3>
                <div className="text-[11px] text-text-muted">{e.founder_email}</div>
                <div className="flex flex-wrap gap-2 mt-3 text-[10px] uppercase tracking-wider font-bold">
                  <Tag label="Brief" v={e.brief_status} />
                  <Tag label="SOW"   v={e.sow_status} />
                </div>
              </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const Tag = ({ label, v }) => (
  <span className="text-[9px] px-1.5 py-0.5 rounded bg-background border border-border">
    <span className="text-text-muted">{label}:</span> <span className="text-text-primary">{(v || 'not_started').replace(/_/g, ' ')}</span>
  </span>
);

export default AgencyHome;
